import { useEffect, useMemo, useRef, useState } from "react";
import { Check, LayoutDashboard, Settings2 } from "lucide-react";
import { Card, LineChart, RankList, StatCard } from "@/ui";
import { genSeries } from "@/mock";
import type { Kpi, Point, Row } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import {
  useSummary,
  useTimeseries,
  useSources,
  useDevices,
  useGeo,
  useDashboards,
  useCreateDashboard,
  useUpdateDashboard,
} from "@/data/hooks";
import type { Summary } from "@/data/services";
import { PanelHeader, rangeLabel } from "../parts";
import { AigenTag, EmptyState, SampleChip, Skeleton, timeLabel } from "../wiring";

/**
 * Custom dashboards (capability #20): the marketer picks which analytics
 * widgets to show and the choice persists to the backend (analytics-svc
 * /dashboards, scoped to the signed-in user within their org), so the selection
 * follows them across browsers. The legacy per-browser localStorage value is
 * migrated once into the user's first server-side dashboard. Each widget renders
 * from the same live analytics hooks the fixed panels use, so the data is real
 * where the property has traffic.
 */
type WidgetKey =
  | "visits"
  | "unique"
  | "conversion"
  | "bounce"
  | "avgSession"
  | "pagesPerVisit"
  | "trend"
  | "sources"
  | "devices"
  | "geo";

type WidgetSize = "kpi" | "half" | "wide";

const WIDGETS: { key: WidgetKey; label: string; size: WidgetSize }[] = [
  { key: "visits", label: "Visits", size: "kpi" },
  { key: "unique", label: "Unique visitors", size: "kpi" },
  { key: "conversion", label: "Conversion rate", size: "kpi" },
  { key: "bounce", label: "Bounce rate", size: "kpi" },
  { key: "avgSession", label: "Avg. session", size: "kpi" },
  { key: "pagesPerVisit", label: "Pages / visit", size: "kpi" },
  { key: "trend", label: "Visitors over time", size: "wide" },
  { key: "sources", label: "Top sources", size: "half" },
  { key: "devices", label: "Devices", size: "half" },
  { key: "geo", label: "Top countries", size: "half" },
];

const STORAGE_KEY = "dl.signals.dashboard.v1";
const DEFAULT_ENABLED: WidgetKey[] = ["visits", "unique", "conversion", "trend", "sources", "devices"];

/** Build a validated enabled-set from a stored config's `widgets` value. An
 *  array (even empty) is honored verbatim, filtered to keys in the catalog;
 *  anything else falls back to the defaults. */
function parseWidgets(raw: unknown): Set<WidgetKey> {
  if (Array.isArray(raw)) {
    return new Set(raw.filter((k): k is WidgetKey => WIDGETS.some((w) => w.key === k)));
  }
  return new Set(DEFAULT_ENABLED);
}

/** One-time migration seed: read the legacy per-browser localStorage selection
 *  (if any) so a returning user's existing widget choice carries into their
 *  first server-side dashboard. After seeding we never touch localStorage again. */
function readMigrationSeed(): WidgetKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) {
        const valid = arr.filter((k): k is WidgetKey => WIDGETS.some((w) => w.key === k));
        if (valid.length > 0) return valid;
      }
    }
  } catch {
    /* ignore */
  }
  return [...DEFAULT_ENABLED];
}

/** Order-independent signature of a selection — used to detect real changes so
 *  hydration and no-op re-renders don't trigger a redundant PATCH. */
function widgetSig(set: Set<WidgetKey>): string {
  return Array.from(set).sort().join(",");
}

function pctChange(cur: number, prev: number | undefined): number {
  if (prev === undefined || prev <= 0) return 0;
  return Math.round(((cur - prev) / prev) * 1000) / 10;
}
function trendOf(d: number): "up" | "down" | "flat" {
  return d > 0 ? "up" : d < 0 ? "down" : "flat";
}

function kpiFor(key: WidgetKey, s: Summary | undefined): Kpi | null {
  if (!s) return null;
  const p = s.previous;
  const mk = (value: number, unit: Kpi["unit"], prev: number | undefined, seed: number, focal = false): Kpi => {
    const delta = pctChange(value, prev);
    return {
      key,
      label: WIDGETS.find((w) => w.key === key)!.label,
      value,
      unit,
      delta,
      trend: trendOf(delta),
      series: genSeries(seed, Math.max(1, value / 7), Math.max(1, value / 30)),
      source: "matomo",
      focal,
    };
  };
  switch (key) {
    case "visits":
      return mk(Math.round(s.visits), "count", p?.visits, 1, true);
    case "unique":
      return mk(Math.round(s.unique_visitors), "count", p?.unique_visitors, 2);
    case "conversion":
      return mk(Math.round(s.conversion_rate * 10) / 10, "pct", p?.conversion_rate, 3);
    case "bounce":
      return mk(Math.round(s.bounce_rate * 1000) / 10, "pct", p?.bounce_rate, 4);
    case "avgSession":
      return mk(Math.round(s.avg_session_seconds), "sec", p?.avg_session_seconds, 5);
    case "pagesPerVisit":
      return mk(Math.round(s.pages_per_visit * 10) / 10, "count", p?.pages_per_visit, 6);
    default:
      return null;
  }
}

const SPAN: Record<WidgetSize, string> = {
  kpi: "col-span-1",
  half: "col-span-2 md:col-span-2",
  wide: "col-span-2 md:col-span-4",
};

export function Dashboard() {
  const { siteId, range, live } = useWorkspace();
  const isLive = !!siteId && live;

  const summaryQ = useSummary(siteId, range);
  const seriesQ = useTimeseries(siteId, range, "visits");
  const sourcesQ = useSources(siteId, range);
  const devicesQ = useDevices(siteId, range);
  const geoQ = useGeo(siteId, range);

  // Per-user dashboards (NOT site-scoped). The first dashboard is the active one.
  const dashQ = useDashboards();
  const createDash = useCreateDashboard();
  const updateDash = useUpdateDashboard();
  const activeDashboard = dashQ.data?.dashboards?.[0];
  const activeId = activeDashboard?.id;

  // Local selection is the render source of truth. It starts at DEFAULT_ENABLED
  // so the panel renders immediately while dashboards load (and stays in-memory
  // if the backend is unreachable / unauthenticated).
  const [enabled, setEnabled] = useState<Set<WidgetKey>>(() => new Set(DEFAULT_ENABLED));
  const [editing, setEditing] = useState(false);

  const hydratedIdRef = useRef<string | null>(null); // which dashboard we've hydrated from
  const createAttemptedRef = useRef(false); // one-shot first-run create guard
  const lastSavedRef = useRef<string | null>(null); // signature of the last known-saved selection

  // Hydrate local selection from the active dashboard's stored config (once per
  // dashboard id). Record the saved signature so the persist effect won't echo
  // it back as a PATCH.
  useEffect(() => {
    if (!activeDashboard) return;
    if (hydratedIdRef.current === activeDashboard.id) return;
    hydratedIdRef.current = activeDashboard.id;
    const hydrated = parseWidgets(activeDashboard.config?.widgets);
    lastSavedRef.current = widgetSig(hydrated);
    setEnabled(hydrated);
  }, [activeDashboard]);

  // First-run + migration: if the caller has no dashboard yet, create one seeded
  // from the legacy localStorage value (else the defaults). Guarded so it fires
  // at most once and never while loading/errored.
  useEffect(() => {
    if (dashQ.isLoading || dashQ.isError) return;
    if (createAttemptedRef.current) return;
    if (dashQ.data && dashQ.data.dashboards.length === 0) {
      createAttemptedRef.current = true;
      const seed = readMigrationSeed();
      setEnabled(new Set(seed)); // reflect the seed immediately, even before the row lands
      createDash.mutate({ name: "My dashboard", config: { widgets: seed } });
    }
  }, [dashQ.isLoading, dashQ.isError, dashQ.data, createDash]);

  // Debounced persist: batch rapid toggles into a single PATCH ~500ms after the
  // last change. Skips the initial hydration (signature match) and the
  // no-backend case (no activeId yet → stays in-memory).
  useEffect(() => {
    if (!activeId || hydratedIdRef.current !== activeId) return;
    const cur = widgetSig(enabled);
    if (cur === lastSavedRef.current) return;
    const t = setTimeout(() => {
      lastSavedRef.current = cur;
      updateDash.mutate({ id: activeId, body: { config: { widgets: Array.from(enabled) } } });
    }, 500);
    return () => clearTimeout(t);
  }, [enabled, activeId, updateDash]);

  const toggle = (k: WidgetKey) =>
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const visible = WIDGETS.filter((w) => enabled.has(w.key));

  const summary = summaryQ.data;
  const seriesPoints: Point[] = useMemo(
    () => (seriesQ.data?.points ?? []).map((p) => ({ t: timeLabel(p.t, range), v: p.value })),
    [seriesQ.data, range],
  );
  const sourceRows: Row[] = (sourcesQ.data?.items ?? []).map((it) => ({
    label: it.source ?? "(direct)",
    value: it.visits,
    pct: 0,
  }));
  const deviceRows: Row[] = (devicesQ.data?.items ?? []).map((it) => ({
    label: it.device ?? "unknown",
    value: it.visits,
    pct: 0,
  }));
  const geoRows: Row[] = (geoQ.data?.items ?? []).map((it) => ({
    label: it.country_name ?? it.country ?? "—",
    value: it.visits,
    pct: 0,
  }));

  function renderWidget(key: WidgetKey) {
    switch (key) {
      case "trend":
        return (
          <Card className="p-5">
            <PanelHeader title="Visitors over time" desc={`First-party visits, ${rangeLabel(range)}.`} />
            <div className="mt-3">
              {seriesPoints.length === 0 ? (
                <EmptyState title="No visits yet" detail="Traffic will show here once it arrives." />
              ) : (
                <LineChart a={seriesPoints} height={220} />
              )}
            </div>
          </Card>
        );
      case "sources":
        return <RankList title="Top sources" rows={sourceRows} accentTop />;
      case "devices":
        return <RankList title="Devices" rows={deviceRows} accentTop />;
      case "geo":
        return <RankList title="Top countries" rows={geoRows} accentTop />;
      default: {
        const kpi = kpiFor(key, summary);
        return kpi ? <StatCard kpi={kpi} /> : null;
      }
    }
  }

  if (summaryQ.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-[124px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="stagger space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PanelHeader title="My dashboard" desc="Your chosen widgets — saved to your account." />
        <div className="flex items-center gap-2">
          {isLive ? <AigenTag /> : <SampleChip />}
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-btn border border-hairline px-2.5 py-1.5 text-[12px] font-medium text-muted transition-colors hover:border-ink/25 hover:text-ink"
          >
            <Settings2 className="h-3.5 w-3.5" strokeWidth={1.75} />
            Customize
          </button>
        </div>
      </div>

      {editing && (
        <Card className="p-4">
          <p className="eyebrow mb-2">Widgets</p>
          <div className="flex flex-wrap gap-2">
            {WIDGETS.map((w) => {
              const on = enabled.has(w.key);
              return (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => toggle(w.key)}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors " +
                    (on
                      ? "border-accent bg-accent-soft text-accent"
                      : "border-hairline text-muted hover:text-ink")
                  }
                >
                  {on && <Check className="h-3 w-3" strokeWidth={2.5} />}
                  {w.label}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {visible.length === 0 ? (
        <div className="grid place-items-center rounded-card border border-dashed border-hairline px-6 py-14 text-center">
          <LayoutDashboard className="mb-2 h-6 w-6 text-subtle" strokeWidth={1.5} />
          <p className="text-[14px] font-medium text-ink-strong">Your dashboard is empty</p>
          <p className="mt-1 text-[13px] text-muted">Click “Customize” to add widgets.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {visible.map((w) => (
            <div key={w.key} className={SPAN[w.size]}>
              {renderWidget(w.key)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
